import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";

import SignInFormMsal from "@/components/auth/SignInFormMsal";
import { loginRequest } from "@/Azure/azure-authentication-config";

// Only allow in-app hash paths so the redirect param can't send users off-site.
const sanitizeRedirect = (raw: string | null): string =>
  raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";

const Login = () => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectTarget = sanitizeRedirect(searchParams.get("redirect"));

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      navigate(redirectTarget, { replace: true });
    }
  }, [isAuthenticated, inProgress, navigate, redirectTarget]);

  const handleSignIn = async () => {
    setBusy(true);
    setError(null);

    try {
      await instance.loginRedirect({
        ...loginRequest,
        // Hash-aware absolute URL so MSAL lands the user back on their
        // intended route after processing the auth code.
        redirectStartPage: `${window.location.origin}${window.location.pathname}#${redirectTarget}`,
      });
    } catch (e: any) {
      console.error("[login] Microsoft sign-in failed:", e);
      setError(
        e?.errorMessage || e?.message || "Sign-in failed. Please try again.",
      );
      setBusy(false);
    }
  };

  return (
    <SignInFormMsal
      onSignIn={handleSignIn}
      busy={busy || inProgress !== InteractionStatus.None}
      error={error}
    />
  );
};

export default Login;
